import { cn } from '@/lib/utils'
import Link from 'next/link'
import type { ImageSize, ImageZone } from './types'

interface Props {
  zones: ImageZone[]
  /** Kích thước ảnh gốc, truyền khi ảnh hiển thị bằng object-cover trong khung khác tỉ lệ */
  imageSize?: ImageSize
  /** Tỉ lệ khung hiển thị (width / height), bắt buộc đi kèm imageSize */
  frameAspect?: number
  className?: string
  /** Prefix ga-tracking-value, mỗi zone gắn `${trackingValue}__zone__${zone.id}` */
  trackingValue?: string
}

/**
 * Quy đổi zone từ toạ độ ảnh gốc sang toạ độ khung hiển thị khi ảnh bị
 * object-cover (cắt 2 bên hoặc trên/dưới). Không truyền imageSize/frameAspect
 * thì coi như ảnh khớp khung, giữ nguyên toạ độ.
 */
export function mapZoneToFrame(
  zone: ImageZone,
  imageSize?: ImageSize,
  frameAspect?: number
) {
  const rect = { x: zone.x, y: zone.y, width: zone.width, height: zone.height }
  if (!imageSize || !frameAspect) return rect

  const imageAspect = imageSize.width / imageSize.height

  if (imageAspect > frameAspect) {
    // Ảnh rộng hơn khung: bị cắt 2 bên
    const visible = frameAspect / imageAspect
    const offset = ((1 - visible) / 2) * 100
    return {
      ...rect,
      x: (rect.x - offset) / visible,
      width: rect.width / visible,
    }
  }

  // Ảnh cao hơn khung: bị cắt trên/dưới
  const visible = imageAspect / frameAspect
  const offset = ((1 - visible) / 2) * 100
  return {
    ...rect,
    y: (rect.y - offset) / visible,
    height: rect.height / visible,
  }
}

export const ImageZonesOverlay = ({
  zones,
  imageSize,
  frameAspect,
  className,
  trackingValue,
}: Props) => {
  if (!zones.length) return null

  return (
    <div className={cn('pointer-events-none absolute inset-0', className)}>
      {zones.map((zone) => {
        const rect = mapZoneToFrame(zone, imageSize, frameAspect)
        const isLeft = zone.labelSide === 'left'

        return (
          <Link
            key={zone.id}
            href={zone.href}
            aria-label={zone.label}
            title={zone.label}
            ga-tracking-value={
              trackingValue ? `${trackingValue}__zone__${zone.id}` : undefined
            }
            ga-tracking-label={zone.label}
            className='group pointer-events-auto absolute rounded-lg outline-none transition-shadow hover:ring-2 hover:ring-white/70 focus-visible:ring-2 focus-visible:ring-white'
            style={{
              left: `${rect.x}%`,
              top: `${rect.y}%`,
              width: `${rect.width}%`,
              height: `${rect.height}%`,
            }}
          >
            {/* Marker: chấm tròn đặt đúng tâm zone, nhãn nằm trái/phải chấm */}
            <span
              className={cn(
                'absolute left-1/2 top-1/2 flex -translate-y-1/2 items-center gap-2',
                isLeft
                  ? '-translate-x-[calc(100%-8px)] flex-row-reverse'
                  : '-translate-x-2'
              )}
            >
              <span className='relative size-4 shrink-0 rounded-full border border-neutral-200'>
                <span className='absolute inset-[20%] rounded-full bg-neutral-200' />
              </span>
              {/* Tag chỉ hiện khi hover (hoặc focus bằng bàn phím) vào zone */}
              <span className='pointer-events-none max-w-[100px] truncate rounded-full bg-neutral-200 px-3 pb-1.5 pt-1 font-criteria text-[10px] text-black opacity-0 transition-opacity duration-200 group-hover:opacity-100 group-focus-visible:opacity-100 lg:w-[142px] lg:max-w-none lg:px-4 lg:pb-2 lg:pt-1.5 lg:text-xs'>
                {zone.label}
              </span>
            </span>
          </Link>
        )
      })}
    </div>
  )
}
